import React from "react";
import { motion } from "framer-motion";
import { FaStar } from "react-icons/fa";
import { MdRocketLaunch } from "react-icons/md";

const benefits = [
  "State-of-the-art science, computer, and music labs",
  "ICSE & ISC affiliated — recognized excellence",
  "Well-stocked library and smart classrooms",
  "Vibrant sports programs and extracurricular activities",
  "A community built on values, growth, and discovery",
  "Every student is a star — nurtured to shine",
];

const Enroll = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative min-h-screen pt-36 md:pt-40 pb-20 px-6 md:px-16 overflow-hidden"
    >
      {/* Ambient glows */}
      <div
        className="absolute top-0 left-0 w-[500px] h-[500px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 right-0 w-96 h-96 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,200,66,0.05) 0%, transparent 70%)" }}
      />

      <div className="max-w-6xl mx-auto">
        {/* Page header */}
        <div className="pt-14 pb-12">
          <span className="section-badge mb-5 inline-flex">Admissions 2025-26</span>
          <h1
            className="uppercase tracking-wide"
            style={{
              fontFamily: "Orbitron, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(1.8rem, 4.5vw, 3.2rem)",
              color: "#f0f5ff",
            }}
          >
            Become a Galaxy Star Student
          </h1>
          <div className="cosmic-divider w-48 mt-4" />
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-14 items-start">
          {/* ── Left: Why Join ── */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:w-1/2"
          >
            <h2
              className="uppercase mb-5"
              style={{
                fontFamily: "Orbitron, sans-serif",
                fontWeight: 800,
                fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)",
                color: "#f0f5ff",
              }}
            >
              How Do I Apply?
            </h2>

            <p
              className="leading-relaxed mb-5"
              style={{ fontFamily: "Nunito, sans-serif", fontSize: "0.95rem", color: "#8899b8" }}
            >
              At Galaxy Star, we pride ourselves on our state-of-the-art facilities,
              dedicated faculty, and holistic approach to education. Our campus is
              built to inspire curiosity, creativity, and character.
            </p>

            <p
              className="leading-relaxed mb-8"
              style={{ fontFamily: "Nunito, sans-serif", fontSize: "0.95rem", color: "#8899b8" }}
            >
              What sets us apart is our commitment to nurturing each child as a unique
              star — encouraging arts, sports, academics, and community engagement in
              equal measure. Join us as we embark on this exciting journey of learning
              and growth. Together, we can help every student reach for the stars!
            </p>

            {/* Benefits list */}
            <div className="space-y-3">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.07 }}
                  className="flex items-start gap-3"
                >
                  <FaStar
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: "#f4c842", fontSize: "12px" }}
                  />
                  <p
                    className="text-sm leading-relaxed"
                    style={{ fontFamily: "Nunito, sans-serif", color: "#94a3b8" }}
                  >
                    {benefit}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── Right: Enrollment Form ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:w-1/2 w-full"
          >
            <div
              className="glass-card-gold p-8 md:p-10 relative overflow-hidden"
            >
              {/* Glow accent */}
              <div
                className="absolute top-0 right-0 w-48 h-48 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(244,200,66,0.06) 0%, transparent 70%)",
                }}
              />

              {/* Form header */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: "rgba(244,200,66,0.12)",
                    border: "1px solid rgba(244,200,66,0.3)",
                  }}
                >
                  <MdRocketLaunch size={20} style={{ color: "#f4c842" }} />
                </div>
                <h2
                  className="font-black"
                  style={{
                    fontFamily: "Orbitron, sans-serif",
                    fontSize: "clamp(0.8rem, 1.5vw, 1rem)",
                    color: "#f0f5ff",
                  }}
                >
                  Become a Galaxy Star
                </h2>
              </div>

              <div className="cosmic-divider mb-7" />

              <form className="space-y-5">
                {/* Full Name */}
                <div>
                  <label
                    className="block text-xs font-bold uppercase tracking-[0.12em] mb-2"
                    style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "#f0f5ff",
                      fontFamily: "Nunito, sans-serif",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "rgba(244,200,66,0.45)";
                      e.currentTarget.style.background = "rgba(244,200,66,0.04)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    className="block text-xs font-bold uppercase tracking-[0.12em] mb-2"
                    style={{ fontFamily: "Nunito, sans-serif", color: "#8899b8" }}
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="contact@email.com"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "#f0f5ff",
                      fontFamily: "Nunito, sans-serif",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "rgba(99,102,241,0.45)";
                      e.currentTarget.style.background = "rgba(99,102,241,0.04)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label
                    className="block text-xs font-bold uppercase tracking-[0.12em] mb-2"
                    style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "#f0f5ff",
                      fontFamily: "Nunito, sans-serif",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "rgba(244,200,66,0.45)";
                      e.currentTarget.style.background = "rgba(244,200,66,0.04)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl text-sm font-black tracking-[0.1em] uppercase transition-all duration-300 hover:scale-[1.02] mt-2"
                  style={{
                    fontFamily: "Nunito, sans-serif",
                    background: "linear-gradient(135deg, #f4c842, #e8ad10)",
                    color: "#020810",
                    boxShadow: "0 0 20px rgba(244,200,66,0.25)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 0 35px rgba(244,200,66,0.45), 0 4px 20px rgba(0,0,0,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 0 20px rgba(244,200,66,0.25)";
                  }}
                >
                  🚀 Submit Enquiry
                </button>
              </form>

              <p
                className="text-center mt-5 text-xs"
                style={{ fontFamily: "Nunito, sans-serif", color: "rgba(136,153,184,0.5)" }}
              >
                We'll get back to you within 24 hours.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default Enroll;
